import { useApolloClient } from '@apollo/client';
import { Table, TextAlign } from '@busybusy/webapp-react-ui';
import type {
  Equipment,
  EquipmentMake,
  EquipmentMakeUpdateInput,
  QueryBasicEquipmentQueryVariables,
} from '__generated__/graphql';
import { SIMPLE_EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import classNames from 'classnames';
import MenuButton from 'components/foundation/buttons/MenuButton/MenuButton';
import EmptyState from 'components/foundation/state-templates/EmptyState/EmptyState';
import ManageListTableDialog from 'containers/jsa-reports/ManageListTableDialog/ManageListTableDialog';
import useEquipmentMakes from 'containers/manage-euipment/hooks/useEquipmentMakes';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useOpenable } from 'hooks';
import { t } from 'i18next';
import { isNil, isNull } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import CreateEquipmentMakeDialog from '../CreateEquipmentMakeDialog/CreateEquipmentMakeDialog';
import EquipmentCatMakeModelDeleteProhibited from '../EquipmentCatMakeModelDeleteProhibitedDialog/EquipmentCatMakeModelDeleteProhibitedDialog';
import './ManageEquipmentMakesDialog.scss';

export interface IManageEquipmentMakesDialogProps {
  className?: ClassName;
  isOpen: boolean;
  onClose: () => void;
}

const ManageEquipmentMakesDialog = (props: IManageEquipmentMakesDialogProps) => {
  const { className, isOpen, onClose } = props;
  const createEditDialog = useOpenable();
  const deleteDialog = useOpenable();
  const deleteProhibitedDialog = useOpenable();

  const client = useApolloClient();

  const { getEquipmentMakes, deleteEquipmentMake } = useEquipmentMakes();

  const [data, setData] = useState<EquipmentMake[]>([]);
  const [selectedRow, setSelectedRow] = useState<EquipmentMake | null>(null);
  const [canDelete, setCanDelete] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const getData = async () => {
    const makes: EquipmentMake[] = await getEquipmentMakes();
    setData(makes);
  };

  const hasConnectedEquipment = async (cat?: EquipmentMake): Promise<boolean> => {
    const make = !isNull(selectedRow) ? selectedRow : cat;

    if (!isNil(make)) {
      const { data } = await client.query<{ equipment: Equipment[] }, QueryBasicEquipmentQueryVariables>({
        query: SIMPLE_EQUIPMENT_QUERY,
        variables: {
          first: 1,
          filter: {
            model: { equipmentMakeId: { equal: make.id } },
            deletedOn: { isNull: true },
          },
        },
        fetchPolicy: 'network-only',
      });

      return data.equipment.length > 0;
    }
    return true;
  };

  const handleSubmit = () => {
    createEditDialog.close();
    getData();
  };

  const handleDelete = async () => {
    createEditDialog.close();
    if (!isNil(selectedRow)) {
      const deleteInput: EquipmentMakeUpdateInput = {
        id: selectedRow.id,
        deletedOn: DateTime.now().toISO(),
      };

      try {
        await deleteEquipmentMake(deleteInput);
        deleteDialog.close();
        getData();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete equipment make:', error);
      }
      setSelectedRow(null);
      setCanDelete(false);
    }
  };

  const onDeleteClicked = async (row: EquipmentMake) => {
    const deleteProhibited = await hasConnectedEquipment(row);
    setSelectedRow(row);
    if (deleteProhibited) {
      deleteProhibitedDialog.open();
    } else {
      deleteDialog.open();
    }
  };

  const getColumns = () => {
    return [
      {
        key: 'actions',
        sort: false,
        cell: (row: EquipmentMake) => {
          return <MenuButton options={[{ label: t('Delete'), onSelect: () => onDeleteClicked(row) }]} />;
        },
        size: '44px',
        cellClassName: 'px-0',
        align: TextAlign.CENTER,
      },
      {
        key: 'title',
        title: 'Make',
        sort: false,
        cell: (row: EquipmentMake) => {
          const make: EquipmentMake = row;
          return <div className="project-title-container">{make.title}</div>;
        },
      },
    ];
  };

  const handleRowClick = async (row: EquipmentMake) => {
    setSelectedRow(row);
    const result = await hasConnectedEquipment(row);
    setCanDelete(!result);
    createEditDialog.open();
  };

  const handleCreateEditDialogClose = () => {
    setSelectedRow(null);
    setCanDelete(false);
    createEditDialog.close();
  };

  const handleDeleteProhibitedClose = () => {
    setSelectedRow(null);
    deleteProhibitedDialog.close();
  };

  const classes = classNames('manage-equipment-makes-dialog', className);

  return (
    <>
      <ManageListTableDialog
        title={t('Makes')}
        onAdd={createEditDialog.open}
        isOpen={isOpen}
        onClose={onClose}
        addText={t('Create')}
        className={classes}
      >
        <Table
          strokeCols={false}
          className={'make-table mb-5'}
          data={data}
          cols={getColumns()}
          onRowClick={handleRowClick}
          emptyTemplate={
            <EmptyState title={t('No Makes')} subtitle={t('Press the Create button above to begin adding makes.')} />
          }
        />
      </ManageListTableDialog>
      <CreateEquipmentMakeDialog
        isOpen={createEditDialog.isOpen}
        onClose={handleCreateEditDialogClose}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        canDelete={canDelete}
        selectedRow={selectedRow}
      />
      <EquipmentCatMakeModelDeleteProhibited
        isOpen={deleteProhibitedDialog.isOpen}
        onClose={handleDeleteProhibitedClose}
        type={'make'}
      />

      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title={t('Delete make?')}
        onClose={deleteDialog.close}
        onDelete={handleDelete}
        showHeaderDivider={false}
      >
        {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
      </DeleteConfirmationDialog>
    </>
  );
};

export default ManageEquipmentMakesDialog;
